import { useCallback, useEffect, useRef, useState } from 'react';
import { FiBell } from 'react-icons/fi';
import OneSignal from 'react-onesignal';

import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { getOrCreateDeviceId } from '@/lib/deviceId';

import { BottomBanner } from './BottomBanner';
import { usePWAInstall } from './usePWAInstall';

const COOLDOWN_KEY = 'push-enable-dismissed-at';
const COOLDOWN_DAYS = 7;

/** Grace inicial: dá tempo do beforeinstallprompt disparar e o banner de
 * instalação assumir a prioridade ANTES deste aparecer — sem pisca-pisca. */
const SHOW_DELAY_MS = 1500;

function isInCooldown(): boolean {
  const dismissedAt = localStorage.getItem(COOLDOWN_KEY);
  if (!dismissedAt) return false;
  const elapsed = Date.now() - parseInt(dismissedAt, 10);
  return elapsed < COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Push só é possível onde a tríade existe. No Safari de iPhone em ABA,
 * `Notification`/`PushManager` não existem (iOS só suporta web push em PWA
 * instalado) — o banner some sozinho no contexto errado.
 */
function isPushCapable(): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Sem OneSignal.init (tenant sem push provider), as Promises do SDK ficam na
 * fila deferred PARA SEMPRE (nem resolvem nem rejeitam). Todo await do SDK
 * passa por aqui para nunca travar o banner.
 */
async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return await Promise.race([
    promise,
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error('push-sdk-timeout')), ms)),
  ]);
}

/**
 * Banner "Ativar notificações" — o prompt de permissão PRECISA nascer de um
 * gesto do usuário no iOS (PWA): o `requestPermission()` automático do
 * useOneSignal é ignorado em silêncio pela Apple. Este banner dispara o
 * prompt no toque e registra o token novo no backend (rearma o device).
 *
 * Estado default = INVISÍVEL: só aparece depois que TODAS as checagens
 * (sync + push-config do tenant + grace do install banner) passarem — nunca
 * renderiza-e-some no F5.
 */
export function EnablePushBanner() {
  const { state } = useAuth();
  // Banner de instalação tem prioridade (mesmo slot fixed bottom): enquanto
  // ele estiver de pé (desktop instalável ou iOS em aba), este não renderiza.
  const { canInstall, isIOSDevice } = usePWAInstall();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'done' | 'error'>('idle');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Elegibilidade: condições síncronas → grace do install banner → gate por
  // tenant (push-config com appId). Só então o banner aparece.
  useEffect(() => {
    if (!state.isAuthenticated) return;
    if (!isPushCapable() || Notification.permission !== 'default' || isInCooldown()) return;

    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const response = await api.get('users/me/push-config');
          const appId = (response.data?.data as { appId?: string } | undefined)?.appId;
          if (!cancelled && appId && mountedRef.current && Notification.permission === 'default') {
            setVisible(true);
          }
        } catch {
          // Sem rede/config: não mostra — próximo load tenta de novo.
        }
      })();
    }, SHOW_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [state.isAuthenticated]);

  const dismiss = useCallback(() => {
    localStorage.setItem(COOLDOWN_KEY, Date.now().toString());
    setVisible(false);
  }, []);

  const activate = useCallback(async () => {
    setBusy(true);
    setPhase('idle');
    try {
      // Gesto do usuário → iOS mostra o prompt nativo aqui.
      await withTimeout(OneSignal.Notifications.requestPermission(), 30_000);

      if (Notification.permission === 'denied') {
        // Negado explicitamente — sem re-prompt possível; não insistir.
        localStorage.setItem(COOLDOWN_KEY, Date.now().toString());
        if (mountedRef.current) setVisible(false);
        return;
      }
      if (Notification.permission !== 'granted') {
        // Prompt fechado sem escolha — mantém o banner para nova tentativa.
        return;
      }

      await withTimeout(OneSignal.User.PushSubscription.optIn(), 15_000);

      // A subscription (e o id novo) pode levar alguns instantes após o grant.
      let token: string | null = null;
      for (let attempt = 0; attempt < 20 && !token && mountedRef.current; attempt++) {
        token = OneSignal.User.PushSubscription.id ?? null;
        if (!token) await new Promise((resolve) => setTimeout(resolve, 500));
      }
      if (!mountedRef.current) return;
      if (!token) {
        setPhase('error');
        return;
      }

      await api.post('users/me/devices', {
        deviceId: getOrCreateDeviceId(),
        token,
      });

      if (!mountedRef.current) return;
      setPhase('done');
      setTimeout(() => {
        if (mountedRef.current) setVisible(false);
      }, 2500);
    } catch (error) {
      console.warn('[EnablePush] Activation failed:', error);
      if (mountedRef.current) setPhase('error');
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, []);

  if (!visible || !state.isAuthenticated) return null;
  if (canInstall || isIOSDevice) return null;

  return (
    <BottomBanner
      testId="enable-push-banner"
      title={phase === 'done' ? 'Notificações ativadas ✓' : 'Ative as notificações'}
      description={
        phase === 'done'
          ? 'Você vai receber avisos de novas mensagens neste aparelho.'
          : phase === 'error'
            ? 'Não foi possível ativar agora — tente novamente.'
            : 'Receba avisos de novas mensagens e atribuições neste aparelho.'
      }
      {...(phase !== 'done'
        ? {
            actionLabel: busy ? 'Ativando…' : phase === 'error' ? 'Tentar de novo' : 'Ativar',
            actionIcon: <FiBell size={14} />,
            onAction: () => { void activate(); },
            actionDisabled: busy,
          }
        : {})}
      onDismiss={dismiss}
    />
  );
}
